import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CertificationsSection } from "@/components/Certifications";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.15 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

const Reveal = ({ children, delay = 0, className = "", ...rest }) => (
  <motion.div
    variants={fadeUp}
    custom={delay}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

const eyebrow = "mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand";
const EyebrowDot = () => <span className="inline-block h-2 w-2 bg-brand" />;

const steps = [
  { n: "01", label: "Customer Requirement" },
  { n: "02", label: "Engineering & Tooling" },
  { n: "03", label: "Quality Check" },
  { n: "04", label: "Production" },
];

export const About = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <main data-testid="about-page">
      {/* 1 — Company Hero */}
      <section ref={heroRef} data-testid="about-hero" className="relative flex min-h-[72vh] items-end overflow-hidden bg-ink">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-105">
          <img src="/assets/about-hero.jpg" alt="CNC machining workshop at Devkii Electricals" className="h-full w-full object-cover object-center" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/40" />

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-14 sm:px-10 lg:px-16 lg:pb-20">
          <motion.p custom={0.1} variants={fadeUp} initial="hidden" animate="visible" className={`${eyebrow} text-white/70`} data-testid="about-eyebrow">
            <EyebrowDot />
            Company — About Us
          </motion.p>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
                Built on experience.
              </motion.span>
            </span>
          </h1>
          <motion.p custom={0.5} variants={fadeUp} initial="hidden" animate="visible" data-testid="about-hero-sub" className="mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            Devkii Electricals brings together precision tooling and manufacturing
            expertise, backed by decades of experience in the industry.
          </motion.p>
        </div>
      </section>

      {/* 2 — Who We Are */}
      <section data-testid="about-who" className="border-b border-ink/10 bg-paper">
        <div className="mx-auto grid w-full max-w-[1400px] gap-12 px-6 py-20 sm:px-10 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-28">
          <Reveal className="lg:col-span-5">
            <span
              aria-hidden
              className="block select-none font-heading text-[6rem] font-black leading-none lg:text-[9rem]"
              style={{ WebkitTextStroke: "1.5px rgba(10,10,10,0.8)", color: "transparent" }}
            >
              2023
            </span>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink/50">
              Incorporated — Devkii Electricals Pvt. Ltd.
            </p>
            <div className="mt-10 bg-brand p-8 text-white lg:max-w-sm">
              <p className="font-heading text-5xl font-black tracking-tight">30+</p>
              <p className="mt-3 text-sm leading-relaxed text-white/85">
                Years of mould-making and switch-manufacture experience — held by the
                company's directors.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className={eyebrow}>
              <EyebrowDot />
              Who we are
            </p>
            <h2 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl">Built on experience.</h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
              Devkii Electricals Pvt. Ltd. was incorporated in 2023. Its directors,
              Amit Panchal and Veeky Panchal, bring more than three decades of experience
              in mould making and switch manufacture — experience that sets the standard
              for precision and tolerance in the company's tooling work.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
              A young company, led by deeply experienced hands.
            </p>
            <div className="mt-10 grid max-w-2xl gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2">
              {[
                { name: "Amit Panchal", role: "Director", img: "/assets/amit-panchal.jpg" },
                { name: "Veeky Panchal", role: "Director", img: "/assets/veeky-panchal.jpg" },
              ].map((d) => (
                <div key={d.name} className="group bg-paper" data-testid={`director-${d.name.split(" ")[0].toLowerCase()}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-ink">
                    <img
                      src={d.img}
                      alt={`${d.name}, ${d.role} — Devkii Electricals`}
                      className="h-full w-full object-cover object-top grayscale transition-[filter] duration-500 group-hover:grayscale-0"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-heading text-xl font-bold tracking-tight">{d.name}</p>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-ink/45">{d.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3 — What Defines Devkii */}
      <section data-testid="about-expertise" className="border-b border-ink/10 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-20 sm:px-10 lg:px-16 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-7">
              <p className={eyebrow}>
                <EyebrowDot />
                What defines Devkii
              </p>
              <h2 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl">
                Precision begins before production.
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
                Tool-room work is Devkii's core business. The company develops and
                manufactures multi-cavity injection moulds for modular switchgear and
                wiring accessories — working from customer part drawings, 3D models, or
                functional specifications developed jointly with customers.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
                Dimensional variation at the cavity surfaces downstream — as flash,
                sticking actuation or early contact failure. So Devkii places the
                tolerance budget where it matters most: at the tooling stage.
              </p>
              <p className="mt-6 border-l-2 border-brand pl-5 font-heading text-lg font-bold tracking-tight text-ink sm:text-xl">
                Precision at the tooling stage is the foundation for consistency in production.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="group aspect-[4/3] overflow-hidden bg-ink">
                <img
                  src="/assets/about-tooling.jpg"
                  alt="Tool-room work on an injection mould at Devkii Electricals"
                  className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                />
              </div>
              <p className="mt-4 text-xs leading-relaxed text-ink/45">
                Series production from Devkii moulds is carried out at an associated manufacturing unit.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="process-flow">
              {steps.map((s) => (
                <div key={s.n} className="group bg-white p-6 transition-colors duration-300 hover:bg-ink lg:p-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand">{s.n}</p>
                  <p className="mt-3 font-heading text-lg font-bold tracking-tight transition-colors duration-300 group-hover:text-white">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Certifications & Registrations */}
      <section className="bg-black text-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-16 sm:px-10 lg:px-16 lg:py-20">
          <CertificationsSection />
        </div>
      </section>

      {/* 6 — Closing CTA */}
      <section data-testid="about-cta-section" className="bg-paper">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-8 px-6 py-20 sm:px-10 lg:flex-row lg:items-end lg:justify-between lg:px-16 lg:py-28">
          <Reveal>
            <h2 className="max-w-2xl font-heading text-4xl font-black tracking-tighter sm:text-5xl">
              Looking for a manufacturing partner?
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-ink/60 sm:text-lg">
              Let's discuss your tooling, OEM or private-label requirements.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-ink"
            >
              Contact Us
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};
